import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { formatter } from '@repo/shared';
import { getRouteApi } from '@tanstack/react-router';

import { useStatisticsByTestEnvironment } from '@/api/generated';
import Header from '@/components/header';
import { Form } from '@/components/ui/form';
import { Spinner } from '@/components/ui/spinner';
import StatisticCard from '@/routes/(app)/(statistics)/components/statistic-card';
import StatisticsFilters from '@/routes/(app)/(statistics)/components/statistics-filters';
import {
  type StatisticsFiltersSchema,
  statisticsFiltersSchema,
} from '@/routes/(app)/(statistics)/lib/schema';

const route = getRouteApi('/app/statistics/$statistic');

const TestEnvironment = () => {
  const { t } = useTranslation();
  const { task, model, date, createdBy } = route.useSearch();
  const form = useForm<StatisticsFiltersSchema>({
    defaultValues: {
      task,
      model,
      date,
      createdBy,
    },
    resolver: zodResolver(statisticsFiltersSchema),
  });

  const { data, isLoading } = useStatisticsByTestEnvironment({
    task,
    model,
    from: date?.from ? new Date(date.from).toISOString() : undefined,
    to: date?.to ? new Date(date.to).toISOString() : undefined,
    createdBy,
  });

  return (
    <Form {...form}>
      <Header
        title={t(`statistics.testEnvironment.title`)}
        description={t(`statistics.testEnvironment.description`)}
      />
      <StatisticsFilters />
      {isLoading ? (
        <Spinner />
      ) : (
        <div className='flex gap-4 flex-wrap items-center'>
          <StatisticCard
            icon={'hash'}
            title={t(`statistics.testEnvironment.totalClusters`)}
            metric={Number(data?.totalClusters ?? 0).toLocaleString()}
            description={t(
              `statistics.testEnvironment.totalClustersDescription`
            )}
          />

          <StatisticCard
            icon={'euro'}
            title={t(`statistics.testEnvironment.totalCost`)}
            metric={formatter.metrics.cost(data?.totalCost ?? 0)}
            description={t(`statistics.testEnvironment.totalCostDescription`)}
          />

          <StatisticCard
            icon={'clock'}
            title={t(`statistics.testEnvironment.avgTime`)}
            metric={formatter.metrics.time(data?.avgTime ?? 0)}
            description={t(`statistics.testEnvironment.avgTimeDescription`)}
          />
        </div>
      )}
    </Form>
  );
};

export default TestEnvironment;
